import { type ReactNode, isValidElement } from 'react';
import type { SortField, TableData } from '@/types';

// NaN values will always be sorted to the end of the list
function getNumberNanValue(direction: 'asc' | 'desc'): number {
  return direction === 'asc' ? Infinity : -Infinity;
}

// Find 'n/a' in string or element
const isNotApplicableValue = (value: string | ReactNode): boolean => {
  if (typeof value === 'string') {
    return value.toLocaleLowerCase() === 'n/a' || value === '' || value === '-';
  }

  if (isValidElement(value)) {
    // Check if it's a NotApplicable component or similar
    const textContent = extractTextFromElement(value);
    return textContent.toLocaleLowerCase() === 'n/a' || textContent === '' || textContent === '-';
  }

  return false;
};

// Extracts text from React element
const extractTextFromElement = (element: ReactNode): string => {
  if (typeof element === 'string') return element;
  if (typeof element === 'number') return element.toString();

  if (isValidElement(element)) {
    if (typeof element.props.children === 'string') {
      return element.props.children;
    }

    if (Array.isArray(element.props.children)) {
      return element.props.children.map(extractTextFromElement).join('');
    }

    return extractTextFromElement(element.props.children);
  }

  return '';
};

export const sortData = <T extends TableData>(tableData: T[], sortField: SortField | null): T[] => {
  if (!sortField) return tableData;

  return [...tableData].sort((a, b) => {
    // Find column values to compare
    const aColumn = a.columns.find((col) => col.id === sortField.key);
    const bColumn = b.columns.find((col) => col.id === sortField.key);

    if (!aColumn || !bColumn) return 0;

    // Extract values from React elements or strings
    const getValueText = (value: string | ReactNode): string => {
      if (typeof value === 'number') return value.toString();
      if (typeof value === 'string') return value;
      if (isValidElement(value)) {
        return extractTextFromElement(value);
      }
      return '';
    };

    const aValue = getValueText(aColumn.value);
    const bValue = getValueText(bColumn.value);

    // Handle numeric sorting
    if (aColumn.type === 'number') {
      const nanValue = getNumberNanValue(sortField.direction);
      const aParsed = parseFloat(aValue.replace(/[^0-9.]/g, ''));
      const bParsed = parseFloat(bValue.replace(/[^0-9.]/g, ''));
      const aValueNum = isNaN(aParsed) ? nanValue : aParsed;
      const bValueNum = isNaN(bParsed) ? nanValue : bParsed;

      return sortField.direction === 'asc' ? aValueNum - bValueNum : bValueNum - aValueNum;
    }

    // Handle date sorting
    if (aColumn.type === 'date') {
      const nanValue = getNumberNanValue(sortField.direction);
      const aValueNum = Date.parse(aValue) || nanValue;
      const bValueNum = Date.parse(bValue) || nanValue;

      return sortField.direction === 'asc' ? aValueNum - bValueNum : bValueNum - aValueNum;
    }

    // Handle percentage sorting
    if (aColumn.type === 'percentage') {
      const aValueNum = parseFloat(aValue.replace('%', '')) || 0;
      const bValueNum = parseFloat(bValue.replace('%', '')) || 0;

      return sortField.direction === 'asc' ? aValueNum - bValueNum : bValueNum - aValueNum;
    }

    // Default string sorting with N/A handling
    const aIsNA = isNotApplicableValue(aColumn.value);
    const bIsNA = isNotApplicableValue(bColumn.value);

    // If both are N/A, they're equal
    if (aIsNA && bIsNA) return 0;

    // If only one is N/A, sort N/A to the bottom regardless of direction
    if (aIsNA) return 1; // a goes to bottom
    if (bIsNA) return -1; // b goes to bottom

    // Normal string comparison for non-N/A values
    return sortField.direction === 'asc'
      ? aValue.localeCompare(bValue)
      : bValue.localeCompare(aValue);
  });
};
